import { lazy, memo } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import Cookies from '@comeon/tomcat-cookies';

import { isBridgeAvailable } from '~/components/app-bridge';
import SuspenseWrapper from '~/components/misc/suspense-wrapper';
import { selectIsLive } from '~/components/we-spin/old-flow/ducks/we-spin-dashboard-duck';

const DGAVerification = lazy(() => import('./dga'));
const WeSpinLive = lazy(() => import('./wespin-live'));

const componentResolver = ({ dga, weSpinLive }) => {
	switch (true) {
		case weSpinLive === 'show':
			return WeSpinLive;
		case dga === 'show':
			return DGAVerification;
		default:
			return null;
	}
};

const mockEnabled = Cookies.get('mockVerification') === 'true';

export const flowBlockerEnabledSelector = createSelector(
	state => state.account.verificationRequired,
	state => state.brand.isDanishFranchise,
	(verificationRequired = !!Cookies.get('verificationRequired'), isDanishFranchise) =>
		verificationRequired && (isBridgeAvailable() || mockEnabled) && isDanishFranchise
);

export const flowBlockerEnabledForWespinSelector = createSelector(
	selectIsLive,
	// can add other details like feature toggle to enable based on franchise
	isWeSpinLive => isWeSpinLive
);

const FlowBlocker = ({ children, onCancel, onComplete }) => {
	const flowBlockEnabled = useSelector(flowBlockerEnabledSelector);
	const isWeSpinLive = useSelector(flowBlockerEnabledForWespinSelector);
	const ComponentToBeRendered = componentResolver({
		dga: flowBlockEnabled && 'show',
		weSpinLive: isWeSpinLive && 'show'
	});

	if (!ComponentToBeRendered) {
		return children;
	}

	return (
		<SuspenseWrapper withSuspense>
			<ComponentToBeRendered onCancel={onCancel} onComplete={onComplete} />
		</SuspenseWrapper>
	);
};

const MemoizedVerification = memo(FlowBlocker);

export default MemoizedVerification;

export const flowBlockerHOC = WrappedComponent => {
	return props => (
		<MemoizedVerification>
			<WrappedComponent {...props} />
		</MemoizedVerification>
	);
};
